import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { RefetchFunction } from '@apollo/client/react/hooks/useSuspenseQuery';
import classNames from 'classnames';
import { Fragment, useState, type CSSProperties } from 'react';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog/index.tsx';
import {
  ANY_SPORT_SERIES_TASK_COMPOSE_TEAM,
  ANY_SPORT_SERIES_TASK_LINEUPS,
} from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSessionReplayShutdown } from '@sorare/core/src/contexts/events/useSessionReplayShutdown.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { useCommonGameSet } from '@sorare/core/src/set';
import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { FullHeightBoard } from '#src/components/boards/FullHeightBoard/index.tsx';
import { HotStreakTile } from '#src/components/boards/HotStreakTile/index.tsx';
import { AnyTaskBasicClaimStory } from '#src/components/missions/AnyTaskBasicClaimStory/index.tsx';
import { HotStreaksTutorialStory } from '#src/components/series/HotStreaksTutorialStory/index.tsx';
import { RefereeExclusiveReward } from '#src/components/series/inviteFriends/RefereeExclusiveReward/index.tsx';
import { ReferrerReward } from '#src/components/series/inviteFriends/ReferrerReward/index.tsx';
import { GuessTheScoreBadge } from '#src/components/squad/GuessTheScoreBadge/index.tsx';

import { SeriesMissionsWidgets } from '../SeriesMissions/SeriesMissionsWidgets/index.tsx';
import { InviteButton } from '../inviteFriends/InviteButton/index.tsx';
import { NextTrckTile } from './NextTrckTile/index.tsx';
import { SeriesBoardHeader } from './SeriesBoardHeader/index.tsx';
import { SetOnboardingNotificationsPrompt } from './SetOnboardingNotificationsPrompt/index.tsx';
import type {
  SeriesBoard_cardSetRoot,
  SeriesBoard_currentUser,
  SeriesBoard_deliverableItemOrder,
} from './__generated__/index.graphql.ts';
import { useMaybeClaimInviteTask } from './useMaybeClaimInviteTask.ts';

const AbsoluteHeader = styled(Vertical)`
  position: absolute;
  width: 100%;
  top: 0;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
  @media ${desktopAndAbove} {
    padding-right: var(--triple-unit);
  }
`;

const BadgesSection = styled(Horizontal)`
  padding: 0 var(--double-unit);
  align-items: flex-start;
  justify-content: flex-end;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  &.withGuessTheScore {
    justify-content: space-between;
  }

  @media ${desktopAndAbove} {
    padding-right: 0;
  }
`;

const MobileDecoration = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(
      to bottom,
      var(--c-nl-500) 0%,
      transparent 300px,
      transparent
    ),
    var(--decoration) no-repeat center center / cover;

  @media ${laptopAndAbove} {
    display: none;
  }
`;

type Props = {
  currentUser: SeriesBoard_currentUser;
  cardSet: SeriesBoard_cardSetRoot;
  deliverableItemOrder?: SeriesBoard_deliverableItemOrder;
  refetch: RefetchFunction<unknown, OperationVariables>;
  showConnectTaskDialog: boolean;
  onConnectTaskDialogClose: () => void;
  onConnectTaskDialogOpen: () => void;
  isReplay: boolean;
};

export const SeriesBoard = ({
  currentUser,
  cardSet,
  deliverableItemOrder,
  refetch,
  showConnectTaskDialog,
  onConnectTaskDialogClose,
  onConnectTaskDialogOpen,
  isReplay,
}: Props) => {
  const { isAndroidApp } = useIsMobileApp();
  const isLaptopAndAbove = useIsLaptop();
  const { generateSportContextPath, sport } = useSportContext();
  const {
    flags: { setThresholdReduction = {} },
  } = useFeatureFlags();
  const commonGameSet = useCommonGameSet();
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const maybeClaimInviteTask = useMaybeClaimInviteTask();
  const { setTasksTrack, setOnboardingTasks, setGuessTheScore } = currentUser;
  const [openHotStreaksTutorialStory, setOpenHotStreaksTutorialStory] =
    useState(false);
  const { steps } = setTasksTrack || {};
  const thresholdTasks = steps?.filter(isTypeFn('ThresholdPickerTask')) || [];
  const [claimedTaskId, setClaimedTaskId] = useState<string | undefined>();

  const [showTargetValue, setShowTargetValue] = useState(false);

  const claimedTask = thresholdTasks.find(task => task.id === claimedTaskId);

  const hasSubmittedAnyHotStreakLineup = thresholdTasks.some(
    task => task.taskLineups.length > 0
  );

  useSessionReplayShutdown(hasSubmittedAnyHotStreakLineup);
  const onboardingTaskCompleted = setOnboardingTasks?.find(
    task => task.aasmState === ManagerProgressionTaskState.COMPLETED
  );
  const [completedOnboardingTask, setCompletedOnboardingTask] = useState<
    typeof onboardingTaskCompleted | null
  >(null);
  if (completedOnboardingTask === null && onboardingTaskCompleted) {
    setCompletedOnboardingTask(onboardingTaskCompleted);
  }

  const [refereeTaskToClaim, setRefereeTaskToClaim] = useState(
    currentUser.inviteTaskAsReferee?.aasmState ===
      ManagerProgressionTaskState.COMPLETED
  );
  const referrerInviteTaskToClaim = currentUser.inviteTasksAsReferrer
    ?.filter(isTypeFn('InviteTaskAsReferrer'))
    .find(task =>
      task.steps?.some(
        step => step.aasmState === ManagerProgressionTaskState.COMPLETED
      )
    );
  const [referrerTaskToClaim, setReferrerTaskToClaim] = useState<
    typeof referrerInviteTaskToClaim | null
  >(null);
  if (referrerTaskToClaim === null && referrerInviteTaskToClaim) {
    setReferrerTaskToClaim(referrerInviteTaskToClaim);
  }
  const showReferrerReward =
    !isReplay &&
    !claimedTask &&
    !completedOnboardingTask &&
    !!referrerTaskToClaim;

  const showRefereeReward =
    !isReplay &&
    !claimedTask &&
    !completedOnboardingTask &&
    !showReferrerReward &&
    !!refereeTaskToClaim;

  const showNotificationsPrompt =
    !isReplay &&
    isAndroidApp &&
    !lifecycle?.[LIFECYCLE.sawPushNotificationsPromptInWebview] &&
    hasSubmittedAnyHotStreakLineup;

  const { backgroundImage } = commonGameSet.visual.play.getSportAssets(sport);
  const { backgroundImage: backgroundImageMobile } =
    commonGameSet.visual.play.getSportAssets(sport, true);

  return (
    <FullHeightBoard
      background={`url(${isLaptopAndAbove ? backgroundImage : backgroundImageMobile})`}
      items={
        isReplay
          ? [
              ...thresholdTasks,
              {
                id: 'replay',
                aasmState: ManagerProgressionTaskState.READY,
              },
            ]
          : thresholdTasks
      }
      renderItem={({ item, index }) => {
        const taskId = idFromObject(item.id);
        const lineupsPath = generateSportContextPath(
          ANY_SPORT_SERIES_TASK_LINEUPS,
          {
            params: { task: taskId },
          }
        );

        const composePath = generateSportContextPath(
          ANY_SPORT_SERIES_TASK_COMPOSE_TEAM,
          {
            params: { task: taskId },
          }
        );
        const thresholdFlagValue =
          setThresholdReduction[sport.toLowerCase()]?.[index];
        const oldThreshold =
          typeof thresholdFlagValue === 'number' ? thresholdFlagValue : null;
        if (!('__typename' in item)) {
          return (
            <NextTrckTile
              taskId={setTasksTrack?.id || ''}
              onTrckClaimed={async () => {
                await refetch();
              }}
              disabled={thresholdTasks.some(
                task => task.aasmState !== ManagerProgressionTaskState.CLAIMED
              )}
            />
          );
        }
        return (
          <HotStreakTile
            task={item}
            showOnboarding={index === 0}
            onClaimTask={setClaimedTaskId}
            onOpenHotStreaksTutorialStory={() => {
              setOpenHotStreaksTutorialStory(true);
            }}
            showTargetValue={showTargetValue}
            setShowTargetValue={setShowTargetValue}
            lineupsPath={lineupsPath}
            composePath={composePath}
            oldThreshold={oldThreshold}
            isReplay={isReplay}
          />
        );
      }}
    >
      {!!isReplay && (
        <MobileDecoration
          style={
            {
              '--decoration': `url(${commonGameSet.visual.board.mobileDecoration})`,
            } as CSSProperties
          }
        />
      )}
      <AbsoluteHeader>
        <SeriesBoardHeader
          squad={currentUser.squad}
          selectedTrack="individual"
          isReplay={isReplay}
        >
          {!isReplay && <InviteButton />}
        </SeriesBoardHeader>
        {!isReplay && (
          <BadgesSection
            className={classNames({
              withGuessTheScore: !!setGuessTheScore,
            })}
          >
            {!!setGuessTheScore && (
              <GuessTheScoreBadge task={setGuessTheScore} />
            )}
            <SeriesMissionsWidgets
              currentUser={currentUser}
              cardSet={cardSet}
              deliverableItemOrder={deliverableItemOrder}
              showConnectTaskDialog={showConnectTaskDialog}
              onConnectTaskDialogClose={onConnectTaskDialogClose}
              onConnectTaskDialogOpen={onConnectTaskDialogOpen}
            />
          </BadgesSection>
        )}
      </AbsoluteHeader>
      {!!openHotStreaksTutorialStory && (
        <HotStreaksTutorialStory
          open={openHotStreaksTutorialStory}
          onClose={() => {
            setOpenHotStreaksTutorialStory(false);
          }}
          taskTrack={setTasksTrack}
        />
      )}
      <SetOnboardingNotificationsPrompt
        open={showNotificationsPrompt}
        onClose={() => {
          updateLifecycle(LIFECYCLE.sawPushNotificationsPromptInWebview, true);
        }}
      />
      {!!claimedTask && (
        <AnyTaskBasicClaimStory
          onClose={() => {
            refetch();
            setClaimedTaskId(undefined);
          }}
          task={claimedTask}
          showContinueButton
        />
      )}
      {!claimedTask && !!completedOnboardingTask && (
        <AnyTaskBasicClaimStory
          onClose={() => {
            refetch();
            setCompletedOnboardingTask(undefined);
            maybeClaimInviteTask(currentUser);
          }}
          task={completedOnboardingTask}
          showContinueButton
          appendedSteps={
            currentUser.inviteTaskAsReferee
              ? [
                  {
                    bodyElement: ({ onNext }) => (
                      <Fragment key="referee-reward-story">
                        <RefereeExclusiveReward
                          currentUser={currentUser}
                          onClose={() => {
                            setRefereeTaskToClaim(false);
                            onNext();
                          }}
                        />
                      </Fragment>
                    ),
                  },
                ]
              : undefined
          }
        />
      )}
      {!!showReferrerReward && (
        <StepperDialog
          onClose={() => {
            refetch();
            setReferrerTaskToClaim(undefined);
          }}
          steps={[
            {
              bodyElement: ({ onNext }) => (
                <Fragment key="referrer-reward-story">
                  <ReferrerReward task={referrerTaskToClaim} onClose={onNext} />
                </Fragment>
              ),
            },
          ]}
        />
      )}
      {!!showRefereeReward && (
        <StepperDialog
          onClose={() => {
            refetch();
            setRefereeTaskToClaim(false);
          }}
          steps={[
            {
              bodyElement: ({ onNext }) => (
                <Fragment key="referee-reward-story">
                  <RefereeExclusiveReward
                    currentUser={currentUser}
                    onClose={onNext}
                  />
                </Fragment>
              ),
            },
          ]}
        />
      )}
    </FullHeightBoard>
  );
};

SeriesBoard.fragments = {
  currentUser: gql`
    fragment SeriesBoard_currentUser on CurrentUser {
      slug
      squad(sport: $sport) {
        id
        ...SeriesBoardHeader_squad
      }
      inviteTaskAsReferee(sport: $sport) {
        id
        aasmState
      }
      inviteTasksAsReferrer(sport: $sport) {
        id
        ...ReferrerReward_inviteTaskAsReferrer
      }
      # eslint-disable-next-line @graphql-eslint/no-deprecated
      setOnboardingTasks(sport: $sport) {
        id
        aasmState
        ...AnyTaskBasicClaimStory_taskInterface
      }
      setGuessTheScore(sport: $sport) {
        id
        ...GuessTheScoreBadge_taskInterface
      }
      setTasksTrack(sport: $sport) {
        id
        steps {
          id
          aasmState
          ... on ThresholdPickerTask {
            id
            replay
          }
          ...HotStreakTile_thresholdPickerTask
          ...AnyTaskBasicClaimStory_taskInterface
        }
        ...HotStreaksTutorialStory_tasksTrack
      }
      ...SeriesMissionsWidgets_currentUser
      ...RefereeExclusiveReward_currentUser
      ...useMaybeClaimInviteTask_currentUser
    }
    ${HotStreakTile.fragments.thresholdPickerTask}
    ${AnyTaskBasicClaimStory.fragments.taskInterface}
    ${HotStreaksTutorialStory.fragments.tasksTrack}
    ${SeriesMissionsWidgets.fragments.currentUser}
    ${SeriesBoardHeader.fragments.squad}
    ${RefereeExclusiveReward.fragments.currentUser}
    ${ReferrerReward.fragments.inviteTaskAsReferrer}
    ${useMaybeClaimInviteTask.fragments.currentUser}
    ${GuessTheScoreBadge.fragments.taskInterface}
  ` as TypedDocumentNode<SeriesBoard_currentUser>,
  cardSet: gql`
    fragment SeriesBoard_cardSetRoot on CardSetRoot {
      id
      ...SeriesMissionsWidgets_cardSetRoot
    }
    ${SeriesMissionsWidgets.fragments.cardSet}
  ` as TypedDocumentNode<SeriesBoard_cardSetRoot>,
  deliverableItemOrder: gql`
    fragment SeriesBoard_deliverableItemOrder on DeliverableItemOrder {
      id
      ...SeriesMissionsWidgets_deliverableItemOrder
    }
    ${SeriesMissionsWidgets.fragments.deliverableItemOrder}
  ` as TypedDocumentNode<SeriesBoard_deliverableItemOrder>,
};
